/**
 * Arrow-key navigation for the design system's list-shaped components.
 *
 * Before 2026-08-23 `tabIndex` appeared **zero** times under
 * `src/Components/DesignSystem/` and no component handled an arrow key. Every
 * `Menu` item, `Dropdown` option and `Tab` was its own tab stop, so reaching the
 * fifth item of a menu meant five Tab presses, and the ARIA roles those
 * components already advertised (`menu`, `listbox`, `tablist`) promised keyboard
 * behaviour that did not exist.
 *
 * Three things this deliberately does **not** do:
 *
 * - **It does not handle Enter or Space.** Every item is a real `<button>`, so the
 *   browser already activates it and fires the click handler. Adding our own
 *   handler would double-fire. The one exception is § 364's: a Space typed in the
 *   middle of a type-ahead search ("new y…") is part of the search, and is consumed.
 * - **It does not cache the item list.** Items are re-read from the DOM on every
 *   keypress, because `Menu` expands submenus in place — a captured list would go
 *   stale the moment a submenu opened.
 * - **It does not touch Tab.** Moving focus out of an open list is left to the
 *   browser; `useDismissable` handles Escape and outside-dismiss. Intercepting Tab
 *   to also close the layer is a real improvement but needs its own change: an
 *   earlier attempt at it dropped focus to `<body>` across 29 call sites.
 */

export type ListOrientation = 'vertical' | 'horizontal';

export interface ListNavOptions {
  /** The element that owns the items. Re-queried on every keypress. */
  container: HTMLElement | null;
  /** Selects the navigable items within `container`, in DOM order. */
  itemSelector: string;
  /** Vertical uses Up/Down, horizontal uses Left/Right. Home/End work in both. */
  orientation?: ListOrientation;
  /** Wrap past the ends. Default true — what `menu` and `listbox` both specify. */
  loop?: boolean;
  /**
   * Jump to the next item starting with what is typed. Default false. Keys within
   * `TYPEAHEAD_RESET_MS` of each other are one search (§ 364), the APG listbox rule.
   */
  typeAhead?: boolean;
  /** Called with the item that should receive focus. Default: focus it. */
  onMove?: (item: HTMLElement, index: number) => void;
  /**
   * Skip disabled items. Default true — a disabled menu row or option is passed over.
   * § 373: `TreeView` passes false, because a node that cannot be SELECTED must still be
   * reachable, or the branch under it could never be opened from the keyboard.
   */
  skipDisabled?: boolean;
}

const itemsOf = (container: HTMLElement | null, selector: string, skipDisabled: boolean): HTMLElement[] =>
  container
    ? [...container.querySelectorAll<HTMLElement>(selector)].filter(
        (el) => !skipDisabled || (!el.hasAttribute('disabled') && el.getAttribute('aria-disabled') !== 'true'),
      )
    : [];

const textOf = (el: HTMLElement) => (el.textContent ?? '').trim().toLowerCase();

/**
 * § 364 — keys typed this close together (ms) extend ONE search; a longer pause starts over.
 * About a second is what the native `<select>` and the APG's listbox examples use.
 */
export const TYPEAHEAD_RESET_MS = 1000;

/** The search typed so far, per list: keyed by the list element, so it goes when the list does. */
const searches = new WeakMap<HTMLElement, { text: string; at: number }>();

/**
 * § 406 — start a list's type-ahead search with text typed BEFORE the list existed: a letter
 * pressed on a closed Dropdown's trigger opens the list on its first match, and the next letter,
 * typed with focus now in the list, continues the same search ("c", "r" → "Crumble") instead of
 * starting a new one from "r".
 */
export function seedTypeahead(container: HTMLElement, text: string): void {
  searches.set(container, { text: text.toLowerCase(), at: Date.now() });
}

/** The first item in `items` whose text starts with `text` — the type-ahead rule, for a caller that has no keydown. */
export function firstTypeaheadMatch(items: HTMLElement[], text: string): HTMLElement | undefined {
  const needle = text.toLowerCase();
  return items.find((el) => textOf(el).startsWith(needle));
}

/**
 * Handle one keydown. Returns `true` if the key was consumed, in which case the
 * caller should not treat it as anything else.
 */
export function handleListNavKey(
  event: { key: string; preventDefault: () => void; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean },
  {
    container,
    itemSelector,
    orientation = 'vertical',
    loop = true,
    typeAhead = false,
    onMove,
    skipDisabled = true,
  }: ListNavOptions,
): boolean {
  const items = itemsOf(container, itemSelector, skipDisabled);
  if (items.length === 0) return false;

  const active = (container?.ownerDocument ?? document).activeElement as HTMLElement | null;
  const current = active ? items.indexOf(active) : -1;

  const move = (index: number) => {
    const item = items[index];
    if (!item) return false;
    event.preventDefault();
    if (onMove) onMove(item, index);
    else item.focus();
    return true;
  };

  const step = (delta: number) => {
    // From "nothing focused", Down/End enter at the top and Up/Home at the bottom.
    if (current === -1) return move(delta > 0 ? 0 : items.length - 1);
    const next = current + delta;
    if (next < 0) return loop ? move(items.length - 1) : false;
    if (next >= items.length) return loop ? move(0) : false;
    return move(next);
  };

  const [prevKey, nextKey] =
    orientation === 'horizontal' ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown'];

  // Moving by arrow or Home / End ends a search: the next letter starts a new one.
  if ([nextKey, prevKey, 'Home', 'End'].includes(event.key) && container) searches.delete(container);

  switch (event.key) {
    case nextKey:
      return step(1);
    case prevKey:
      return step(-1);
    case 'Home':
      return move(0);
    case 'End':
      return move(items.length - 1);
    default:
      break;
  }

  if (!typeAhead || !container) return false;
  // A single printable character, and no modifier that would make it a shortcut.
  if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return false;

  /*
    § 364 — the keys typed in quick succession are ONE search. Each key used to be its own
    search, so typing "cru" in [Cheddar, Cream, Crumble] went to Cream on "c", then looked for
    an item starting with "r", then "u", and stopped on Cream, never reaching Crumble.
  */
  const now = Date.now();
  const previous = searches.get(container);
  const ongoing = previous !== undefined && now - previous.at <= TYPEAHEAD_RESET_MS;
  /* Space starts nothing: with no search under way it is the key that ACTIVATES the item, and
     the item's own button handles it. Inside a search ("new y…") it is part of the name, and it
     is consumed, so the focused item is not pressed halfway through a word. */
  if (event.key === ' ' && !ongoing) return false;
  const text = `${ongoing ? previous.text : ''}${event.key.toLowerCase()}`;
  searches.set(container, { text, at: now });
  if (event.key === ' ') event.preventDefault();

  /* One character pressed repeatedly cycles through that character's matches (the `<select>`
     rule), searching from the item AFTER the current one, exactly as a single key always did.
     A longer string searches from the current item itself, so "p" lands on Porto and "po"
     stays there instead of moving on. */
  const repeated = [...text].every((ch) => ch === text[0]);
  const needle = repeated ? text[0] : text;
  const start = current === -1 ? 0 : current + (repeated ? 1 : 0);
  for (let offset = 0; offset < items.length; offset += 1) {
    const index = (start + offset) % items.length;
    if (textOf(items[index]).startsWith(needle)) return move(index);
  }
  return event.key === ' ';
}
